import React, { useState } from 'react';

import type { CreateStreamRequest, StreamRecord } from '@vlabs/api/artemy/model';
import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { useForm } from 'react-hook-form';

import { loadGroupOptions } from '@vlabs/monokit/entities/sources';
import type { SourceGroupOption } from '@vlabs/monokit/entities/sources';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';

import { useDuplicateStreamTranslation } from '../i18n';
import type { DuplicateStreamProps } from '../types';
import st from './DuplicateStreamForm.module.sass';

const displayName = 'DuplicateStreamForm';
const testid = buildTestid(displayName);

interface DuplicateStreamFormProps extends DuplicateStreamProps {
  stream: StreamRecord;
  onCancel: VoidFunction;
  onSubmit: (data: CreateStreamRequest) => Promise<void>;
  isPending: boolean;
}

interface DuplicateStreamFormValues {
  name: string;
  reference: string;
}

function getStreamType(stream: StreamRecord): 'stream' | 'videofile' {
  const type = (stream.data as { type?: unknown } | undefined)?.type;
  return type === 'videofile' ? 'videofile' : 'stream';
}

function DuplicateStreamForm({
  stream,
  groupId,
  onCancel,
  onSubmit,
  isPending,
}: DuplicateStreamFormProps): React.JSX.Element {
  const { t } = useDuplicateStreamTranslation();
  const [streamType, setStreamType] = useState<'stream' | 'videofile'>(getStreamType(stream));
  const [groupOption, setGroupOption] = useState<SourceGroupOption | undefined>(undefined);

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<DuplicateStreamFormValues>({
    defaultValues: {
      name: stream.name ?? '',
      reference: (stream.data as { reference?: string } | undefined)?.reference ?? '',
    },
  });

  React.useEffect(() => {
    loadGroupOptions().then((options) => {
      const preselected = options.find((option) => option.value === groupId);
      if (preselected) setGroupOption(preselected);
    });
  }, [groupId]);

  const analytics = stream.analytics ?? [];

  const submit = handleSubmit(async (values: DuplicateStreamFormValues) => {
    if (!groupOption) return undefined;

    return onSubmit({
      name: values.name,
      data: {
        type: streamType,
        reference: values.reference,
      },
      group_id: groupOption.value,
      analytics,
    });
  });

  return (
    <form onSubmit={submit}>
      <div className={st.Root}>
        <h2 className={st.Title}>
          {t('duplicate_stream_form.title')}
        </h2>

        <div className={st.Field}>
          <Control.Input
            errorMessage={errors.name?.message}
            errors={errors}
            id="name"
            label={t('duplicate_stream_form.name')}
            {...register('name', { required: t('duplicate_stream_form.validation.required') })}
            {...testid('name')}
          />
        </div>

        <div className={st.Field}>
          <Select
            classNamePrefix="DuplicateStreamType"
            label={t('duplicate_stream_form.type')}
            onChange={(option) => setStreamType(option?.value ?? 'stream')}
            options={[
              { label: t('duplicate_stream_form.stream_type_stream'), value: 'stream' },
              { label: t('duplicate_stream_form.stream_type_videofile'), value: 'videofile' },
            ]}
            value={{ label: t(`duplicate_stream_form.stream_type_${streamType}`), value: streamType }}
            {...testid('streamType')}
          />
        </div>

        <div className={st.Field}>
          <Control.Input
            errorMessage={errors.reference?.message}
            errors={errors}
            id="reference"
            label={t('duplicate_stream_form.reference')}
            {...register('reference', { required: t('duplicate_stream_form.validation.required') })}
            {...testid('reference')}
          />
        </div>

        <div className={st.Field}>
          <Select
            classNamePrefix="DuplicateStreamGroup"
            defaultOptions
            isAsync
            label={t('duplicate_stream_form.group')}
            loadOptions={loadGroupOptions}
            menuPortalTarget={document.body}
            onChange={(option) => setGroupOption(option ?? undefined)}
            placeholder={t('duplicate_stream_form.group')}
            styles={selectMenuPortalStyles}
            value={groupOption}
            {...testid('group')}
          />
        </div>

        <div className={st.Analytics}>
          <span className={st.AnalyticsLabel}>{t('duplicate_stream_form.analytics')}</span>
          {analytics.length === 0 && (
            <span className={st.AnalyticsEmpty}>—</span>
          )}
          {analytics.map((item, index) => (
            <div
              className={st.AnalyticsItem}
              key={String(item.label ?? item.type ?? index)}
            >
              {item.label ?? String(item.type)}
            </div>
          ))}
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={isPending || !groupOption}
            isLoading={isPending}
            type="submit"
          >
            {t('duplicate_stream_form.on_submit')}
          </Control.Button>

          <Control.Button
            className={st.Action}
            disabled={isPending}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('duplicate_stream_form.on_cancel')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}

DuplicateStreamForm.displayName = displayName;

export { DuplicateStreamForm };
